import type { CSSProperties } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuthFlow } from '@/hooks';
import { GameCategory } from '@sportspredict/shared';
import { cn, getCategoryName, getValidImgVariant } from '@/utils';
import { useGetProbabilityEventDetailsQuery } from '../../store/api/probability-event.api';

/** Leagues the banner names by their short label rather than the category's display name. */
const LEAGUE_LABELS: Partial<Record<string, string>> = {
	[GameCategory.NFL]: 'NFL',
	[GameCategory.EPL]: 'EPL',
};

/** Design gap between the banner and the page content on mobile. */
const MOBILE_CONTENT_GAP_PX = 16;

interface ProbabilityEventBannerProps {
	eventId: string;
	eventTitle?: string | null;
	categoryId?: string | null;
	/** How much taller the mobile header is inside the app than the space the layout reserves for it. */
	appHeaderOffsetPx?: number;
	/**
	 * Mobile: how far below the top of its area the page's first content sits
	 * on its own, on the web and in the app. The banner leaves exactly
	 * MOBILE_CONTENT_GAP_PX between itself and that content.
	 */
	pageTopSpacePx?: { web: number; app: number };
	/** Desktop: the page's content container classes, so the text lines up with the content. */
	desktopContainerClassName?: string;
}

/**
 * The event's admin-uploaded banner, full width under the header on the main
 * probability pages: the image darkened for legibility, with the category and
 * event title on top. Renders nothing when the event has no banner.
 */
export function ProbabilityEventBanner({
	eventId,
	eventTitle,
	categoryId,
	appHeaderOffsetPx = 0,
	pageTopSpacePx = { web: 0, app: 0 },
	desktopContainerClassName,
}: ProbabilityEventBannerProps) {
	const { isWTN } = useAuthFlow();
	const { data: eventDetails } = useGetProbabilityEventDetailsQuery(
		eventId ? { eventId } : skipToken
	);

	const mobileImage = getValidImgVariant(eventDetails?.mobileBannerImgVariants);
	const desktopImage = getValidImgVariant(eventDetails?.desktopBannerImgVariants);
	// Each size shows only its own upload: without a desktop image there is no
	// banner on desktop, and without a mobile image none on mobile.
	if (!mobileImage && !desktopImage) return null;

	const categoryName = (categoryId && LEAGUE_LABELS[categoryId]) || getCategoryName(categoryId);
	const appHeaderOffset = isWTN && appHeaderOffsetPx > 0 ? appHeaderOffsetPx : 0;

	return (
		<section
			aria-label={eventTitle ? `${eventTitle} banner` : 'Event banner'}
			className={cn(
				// Desktop: pages already leave room under the header, the banner adds
				// 8px to reach the 32-40px design gap. Mobile: the banner sits right
				// under the header (moved down by the app's taller header) and pulls
				// the page up so exactly 16px separate it from the content.
				'relative h-12 w-full overflow-hidden lg:mb-2 lg:h-20',
				'max-lg:mb-(--banner-mobile-mb) max-lg:mt-(--banner-mobile-mt)',
				!mobileImage && 'max-lg:hidden',
				!desktopImage && 'lg:hidden'
			)}
			style={
				{
					'--banner-mobile-mt': `${appHeaderOffset}px`,
					'--banner-mobile-mb': `${MOBILE_CONTENT_GAP_PX - (isWTN ? pageTopSpacePx.app : pageTopSpacePx.web)}px`,
				} as CSSProperties
			}
		>
			<picture>
				{mobileImage && desktopImage && (
					<source media="(min-width: 1024px)" srcSet={desktopImage} />
				)}
				<img
					src={mobileImage || desktopImage || undefined}
					alt=""
					className="absolute inset-0 size-full object-cover"
					decoding="async"
				/>
			</picture>
			<div className="absolute inset-0 bg-black/32" />

			<div
				className={cn(
					'relative mx-auto flex h-full w-full items-center gap-2 px-4',
					desktopContainerClassName
				)}
			>
				{categoryName && (
					<span className="flex h-6 shrink-0 items-center rounded-[20px] bg-[rgba(172,192,246,0.32)] pl-[10px] pr-2 font-['DM_Sans'] text-[12px] font-medium leading-[14px] tracking-[-0.12px] text-[#ACC0F6] drop-shadow-[0px_2px_10px_rgba(0,0,0,0.06)]">
						{categoryName}
					</span>
				)}
				{eventTitle && (
					<p className="orbitron min-w-0 truncate text-[14px] font-bold leading-[22px] tracking-[1.2px] text-white lg:text-[20px] lg:leading-[28px]">
						{eventTitle}
					</p>
				)}
			</div>
		</section>
	);
}
